// @flow

import { toASCII } from 'punycode.js';
import { disbatch } from 'redux-act';

import { selectPricingInfos } from '@gandi/billing';
import {
  selectOneProduct,
  createCart,
  updateProductAndGetCartOrNotify,
  getCart,
  addCartProduct,
  removeCartProduct,
  addCartProducts,
  addCartBulkDiscount,
  addProductOptimistic,
  removeProductOptimistic,
  selectCartUUID as selectCartId,
  selectCartIsActive,
  selectCartIsBeingCreated,
  selectProductsByTypeAndProcess,
  type ProductData,
} from '@gandi/cart';

// @TODO: don't import from a private folder.
// eslint-disable-next-line @gandi/gandi/no-private-import
import type { AddProductsArgs } from '@gandi/cart/dist/modules/actions';
import type { TranslatorProps } from '@gandi/translate';

import { logging } from 'gandi.redux-logger';

import type { Dispatch, GetState } from '../../../types';
import type { PhasePrice, ExtendedSuggestion } from '../../types';
import { getMinDuration } from '../helpers/suggestions';
import {
  selectHasWelcomeOffer,
  selectAllWelcomeOfferCode,
  selectAreWelcomeOfferCodesInCart,
} from '../selectors/discount';
import { selectFilterValue } from '../selectors/filters';
import { selectPosition } from '../selectors/results';
import { selectSearchValue, selectSearchUUID } from '../selectors/search';

import { flagOptimistic, unflagOptimistic } from './results';

const loggerNamespace = 'DomainCreate/modules/actions/cart';

const getPrice = (
  prices: PhasePrice,
  taxesIncluded: boolean,
  duration: number = 1,
) => {
  const kind = taxesIncluded ? 'afterTaxes' : 'beforeTaxes';

  return (
    parseFloat(
      prices.discount ? prices.discount.price[kind] : prices.price?.[kind],
    ) * duration
  );
};

export const addWelcomeOfferDiscountsToCart =
  (): ((dispatch: Dispatch, getState: GetState) => any) =>
  (dispatch: Dispatch, getState: GetState) => {
    const discounts = selectAllWelcomeOfferCode(getState()).map(code => ({
      code,
    }));
    // $FlowFixMe[prop-missing]
    return dispatch(addCartBulkDiscount(discounts)).catch(() => {});
  };

// XXX: it's almost the same as `AddProductsArgs` this could be refactored
type AddProductsArgsBis = {
  productsData: AddProductsArgs['productsData'],
  extras: AddProductsArgs['extra'],
};

export const addManyToCart =
  (
    results: Array<ExtendedSuggestion>,
    dryRun: boolean = false,
  ): ((dispatch: Dispatch, getState: GetState) => Promise<any>) =>
  async (dispatch: Dispatch, getState: GetState) => {
    const searchUUID = selectSearchUUID(getState());
    const tldFilter = selectFilterValue(getState(), 'tlds')
      .split(',')
      .filter(tld => tld !== 'suggested');

    const domainCreateInCart = selectProductsByTypeAndProcess(getState(), {
      productType: 'domain',
      process: 'create',
    });

    const datas: AddProductsArgsBis = results.reduce((acc, current) => {
      const productData: ProductData = {
        process: 'create',
        product_type: 'domain',
        payload: {
          fqdn: toASCII(current.fqdn),
          tld: current.tld,
          tld_phase: current.phase,
          corporate: current.corporate,
          duration: getMinDuration(current),
          mailbox_optin: domainCreateInCart.length < 10,
        },
        metadatas: {
          corporate: current.corporate,
          fqdn: current.fqdn,
          query: selectSearchValue(getState()),
          position: selectPosition(getState(), current.fqdn),
          selected_filter: tldFilter,
          real_position: undefined,
          lang: undefined,
          searchUUID,
        },
      };

      return ({
        productsData: acc.productsData
          ? [...acc.productsData, productData]
          : [productData],
        extras: {
          ...acc.extras,
          [current.fqdn]: {
            label: current.fqdn,
          },
        },
      }: AddProductsArgsBis);
      // $FlowFixMe[prop-missing]
    }, ({}: AddProductsArgsBis));

    return dispatch(
      addCartProducts({
        productsData: datas.productsData,
        extra: datas.extras,
        options: {
          headers: { 'X-Dry-Run': Number(dryRun) },
        },
      }),
    ).then(async () => {
      const welcomeOffer =
        !selectAreWelcomeOfferCodesInCart(getState()) &&
        results.some(products =>
          selectHasWelcomeOffer(getState(), products.tld),
        );

      if (welcomeOffer) {
        await dispatch(addWelcomeOfferDiscountsToCart());
      }
    });
  };

/**
 * Optimistically add a product to the Cart.
 * Do not wait for API response - add product to Cart
 * Cart state is updated and CartWidget is rendered immediately with
 * data coming from the suggestion.
 */
const optimisticAddToCart =
  (suggestion: {
    ...ExtendedSuggestion,
    id?: string,
    status?: 'optimistic' | string,
    mailbox_optin?: boolean,
  }): (Dispatch => any) =>
  dispatch => {
    // $FlowFixMe[incompatible-use]
    const prices: PhasePrice = suggestion.prices.create[suggestion.phase];
    // $FlowFixMe[incompatible-call]
    const duration = getMinDuration(suggestion);

    return dispatch(
      addProductOptimistic({
        /**
         * Our Cart product won't have an `id` until it has been accepted by the API.
         */
        id: suggestion.id,
        status: suggestion.status,
        label: suggestion.fqdn,
        productType: 'domain',
        process: 'create',
        payload: {
          tld: suggestion.tld,
          fqdn: toASCII(suggestion.fqdn),
          duration,
          tld_phase: suggestion.phase,
          mailbox_optin: suggestion?.mailbox_optin ?? true,
        },
        // Use the suggestion price and taxes
        priceAfterTaxes: getPrice(prices, true, duration),
        priceBeforeTaxes: getPrice(prices, false, duration),
        taxes: suggestion.taxes,

        // Flag the product as being a promotion or a premium in the same way the Cart API does.
        isPremium: suggestion.premium,
        // $FlowFixMe
        isPromotion: !!suggestion.prices.create.hasDiscount,

        currency: suggestion.currency,
      }),
    );
  };

type OptimisticAddon = {
  id?: string,
  status?: 'optimistic' | string,
};
type SuggestionForCart = {
  ...ExtendedSuggestion,
  ...OptimisticAddon,
};
/**
 * Proxy the product addition to the Cart.
 * Note: Cart needs to be created and fetched before adding any product (even optimistic) to it.
 * Optional: Add the product optimistically while waiting for the API
 * @param {{
      optimistic?: boolean,
      UIPosition?: mixed,
      lang?: string,
      ...
    }}  options               Options.
 * @param {Boolean} options.optimistic    Also do optimistic add to cart or not.
 */
export const addToCart =
  (
    suggestion: SuggestionForCart,
    options: {
      optimistic?: boolean,
      UIPosition?: mixed,
      lang?: string,
      ...
    },
  ): ((dispatch: Dispatch, getState: GetState) => Promise<mixed>) =>
  (dispatch: Dispatch, getState: GetState) => {
    const logger = logging(dispatch, loggerNamespace);

    const state = getState();
    // patch cart in case of welcome offer
    const welcomeOffer =
      selectHasWelcomeOffer(state, suggestion.tld) &&
      !selectAreWelcomeOfferCodesInCart(state);

    const punycodedSuggestion = {
      ...suggestion,
      fqdn: toASCII(suggestion.fqdn),
    };

    // Immediately set the suggestion as optimistic,
    // even if a Cart needs to be created, or one is already being created.
    if (options.optimistic && !welcomeOffer) {
      // $FlowFixMe[not-an-array]
      dispatch(flagOptimistic(punycodedSuggestion.fqdn));
    }

    // $FlowFixMe[incompatible-call]
    const { currency, country } = selectPricingInfos(state);

    const cartExists = selectCartId(state) && selectCartIsActive(state);
    const cartIsBeingCreated =
      selectCartIsBeingCreated(state) ||
      (selectCartId(state) && !selectCartIsActive(state));

    // Wait for the Cart to finish being created before adding the product,
    // otherwise we might create multiple carts ...
    if (cartIsBeingCreated) {
      logger.info(
        `A cart is already being created, waiting completion before adding product ${suggestion.fqdn}`,
      );
      let intervalID;

      const cartCreation = new Promise(resolve => {
        const delay = 300;
        intervalID = setInterval(() => {
          if (selectCartIsActive(getState())) {
            resolve();
          }
        }, delay);
      });

      // Once the cartCreation Promise is resolved, resume adding the product to the Cart
      return cartCreation
        .then(() => {
          clearInterval(intervalID);
          return dispatch(addToCart(punycodedSuggestion, options));
        })
        .catch(err => {
          logger.error(
            { err },
            'domain create product could not be added to the cart, cartCreation was rejected',
          );

          throw err;
        });
    }

    // Is there a Cart already?
    if (!cartExists) {
      return dispatch(createCart({ currency, country }))
        .then(() => dispatch(getCart()))
        .then(() => dispatch(addToCart(punycodedSuggestion, options)));
    }

    if (options.optimistic && !welcomeOffer) {
      // Optimistic product can be added to the Cart as we have one already
      dispatch(optimisticAddToCart(punycodedSuggestion));
      /*
       * Optimistic product was added to the Cart and given a client-side generated ID.
       * FIXME: We need to pass that ID to the addToCart action so the product from the API
       * can properly replace the optimistic product.
       * Otherwise we'll end up with 2 products in the cart state.
       */
      const optimisticProduct = selectOneProduct(getState(), {
        fqdn: punycodedSuggestion.fqdn,
      });
      const optimisticProductId = optimisticProduct && optimisticProduct.id;

      // This is the last time we call the `addToCart` action,
      // the product addition to the Cart will be attempted through the API.
      const finalOptions = {
        ...options,
        optimistic: false,
      };

      return dispatch(
        addToCart(
          // $FlowFixMe[incompatible-call] number vs string
          { id: optimisticProductId, ...punycodedSuggestion },
          finalOptions,
        ),
      );
    }
    const searchUUID = selectSearchUUID(state);

    const tldFilter = selectFilterValue(getState(), 'tlds')
      .split(',')
      .filter(tld => tld !== 'suggested');

    const phase = suggestion.prices?.create?.phases?.find(
      i => i.name === suggestion.phase,
    );

    const domainCreateInCart = selectProductsByTypeAndProcess(getState(), {
      productType: 'domain',
      process: 'create',
    });

    const productData = {
      product_type: 'domain',
      process: 'create',
      payload: {
        tld: suggestion.tld,
        fqdn: toASCII(suggestion.fqdn),
        // $FlowFixMe[incompatible-call]
        duration: getMinDuration(suggestion),
        tld_phase: suggestion.phase,
        lang: undefined,
        corporate: suggestion.corporate,
        phase_date_start: phase?.starts_at,
        phase_date_end: phase?.ends_at,
        mailbox_optin: domainCreateInCart.length < 10,
      },
      metadatas: {
        corporate: suggestion.corporate,
        fqdn: suggestion.fqdn,
        query: selectSearchValue(getState()),
        position: selectPosition(getState(), suggestion.fqdn),
        selected_filter: tldFilter,
        real_position: undefined,
        lang: undefined,
        searchUUID,
      },
    };

    if (options.UIPosition) {
      // $FlowFixMe[incompatible-type]
      productData.metadatas.real_position = options.UIPosition;
    }

    // In case of an IDN, a lang can optionally be passed in the product payload.
    if (options.lang) {
      // $FlowFixMe[incompatible-type]
      productData.payload.lang = options.lang;
      // $FlowFixMe[incompatible-type]
      productData.metadatas.lang = options.lang;
    }

    const extra = {
      label: suggestion.fqdn,
      optimisticId: suggestion.id,
    };

    // Add the product directly via the API.
    // Ask the Cart API to not perform Dry-Run (or pricing, see T29315)
    return Promise.resolve()
      .then(() => welcomeOffer && dispatch(addWelcomeOfferDiscountsToCart()))
      .then(() =>
        dispatch(
          addCartProduct({
            productData,
            extra,
            options: {
              headers: { 'X-Dry-Run': 0 },
            },
          }),
        ),
      )
      .then(() => dispatch(getCart()))
      .then(() => {
        // $FlowFixMe[not-an-array]
        dispatch(unflagOptimistic(punycodedSuggestion.fqdn));
      })
      .catch(err => {
        // $FlowFixMe[not-an-array]
        dispatch(unflagOptimistic(punycodedSuggestion.fqdn));

        const optimisticProduct = selectOneProduct(getState(), {
          fqdn: punycodedSuggestion.fqdn,
        });
        const optimisticProductId = optimisticProduct && optimisticProduct.id;

        dispatch(removeProductOptimistic(optimisticProductId));
        logger.error(
          { err },
          'Unable to add domain create product to the cart',
        );

        return Promise.reject(err);
      });
  };

export const updateProductPhase =
  (
    __: TranslatorProps['__'],
    suggestion: ExtendedSuggestion,
    phase: string,
  ): ((dispatch: Dispatch, getState: GetState) => any) =>
  (dispatch: Dispatch, getState: GetState) => {
    const product = selectOneProduct(getState(), { fqdn: suggestion.fqdn });

    const newPayload = {
      ...product?.payload,
      tld_phase: phase,
    };

    // DO NOT USE CART MIDDLEWARE HERE.
    // Ask the Cart API to not perform Dry-Run (or pricing, see T29315)
    return dispatch(
      // $FlowFixMe use Phase instead of string
      updateProductAndGetCartOrNotify(__, product.id, newPayload, {
        headers: { 'X-Dry-Run': 0 },
      }),
    );
  };

/**
 * Proxy the product removal to the Cart.
 * Optional: Remove the product optimistically while waiting for the API
 * @param {{ optimistic?: boolean, ... }}  options               Options.
 * @param {Boolean} options.optimistic    Also do optimistic remove from cart or not.
 */
export const removeFromCart =
  (
    suggestion: ExtendedSuggestion,
    options: {
      optimistic?: boolean,
      UIPosition?: any,
      lang?: any,
    } = {},
  ): ((dispatch: Dispatch, getState: GetState) => Promise<mixed>) =>
  (dispatch: Dispatch, getState: GetState) => {
    const logger = logging(dispatch, loggerNamespace);
    const state = getState();
    const product = selectOneProduct(state, { fqdn: suggestion.fqdn });

    const domainCreateInCart = selectProductsByTypeAndProcess(state, {
      productType: 'domain',
      process: 'create',
    });

    if (product && product.id) {
      logger.info(`Removing ${suggestion.fqdn} (${product.id}) via API.`);
      // Ask the Cart API to not perform Dry-Run - see T29315
      const removal = dispatch(
        removeCartProduct(product.id, { headers: { 'X-Dry-Run': 0 } }),
      )
        .then(() => dispatch(getCart()))
        // TODO: when removing whole optimistic logic, we should be able to unplug following actions
        .then(() => {
          // $FlowFixMe[not-an-array]
          dispatch(unflagOptimistic(suggestion.fqdn));
          return Promise.resolve();
        })
        .catch(() => {
          dispatch(
            // $FlowFixMe
            optimisticAddToCart({
              ...suggestion,
              id: product.id,
              status: product.status,
              mailbox_optin: domainCreateInCart.length < 10,
            }),
          );
          // $FlowFixMe[not-an-array]
          dispatch(unflagOptimistic(suggestion.fqdn));
        });

      if (options.optimistic) {
        logger.info(`Attempt to remove ${suggestion.fqdn} optimistically`);
        disbatch(
          dispatch,
          // $FlowFixMe[not-an-array]
          unflagOptimistic(suggestion.fqdn),
          removeProductOptimistic(product.id),
        );

        logger.info(
          `Set suggestion as added optimistically ${suggestion.fqdn}.`,
        );
        logger.info(`Removed optimistically ${suggestion.fqdn}.`);
      }

      return removal;
    }

    if (product && !product.id) {
      return Promise.reject(
        new Error(
          `Product ${suggestion.fqdn} does not have an ID, refusing to remove.`,
        ),
      );
    }

    return Promise.reject(
      new Error(`Could not find product ${suggestion.fqdn} in Cart.`),
    );
  };
